import { fetchAccessorialsComponents, fetchAccountAccessorials } from "../api/Accessorials";
import { fetchAccountConfigs } from "../api/Account";
import { fetchRateSheet } from "../api/Billing";
import { fetchAccountAndRateSheetLos, fetchAccountShortLos } from "../api/Los";
import { fetchUndeliveredCount } from "../api/OrdersApi";
import { isEmpty } from "../common/Common";
import { renderAlertMessage, updatePicSettingsWithBol } from "./common";
import { appendNoneToLosList } from "./billing";

export const getLevelOfServices = async (
  accountCode,
  type_of_order,
  label_code = '',
  loadingFunction = () => {}
) => {
  if (!isEmpty(accountCode)) {
    loadingFunction(true);
    try {
      const result = await fetchAccountShortLos(
        accountCode,
        type_of_order,
        "account_code",
        label_code
      );
      if (result.success) {
        // idempotent — avoids a duplicate NONE when the backend already returned its flagged NONE
        return {
          success: true,
          data: appendNoneToLosList(result.account.account_los || []),
        };
      } else {
        renderAlertMessage(result.errors);
        return { success: false, data: [], errors: result.errors || [] };
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error; // Propagate the error to be handled by the caller
    } finally {
      loadingFunction(false);
    }
  }
};

// Linehaul routing tariffs only — NOT pickup/delivery LOS. Uses
// account_los_short_list with type_of_order=LH (los_type LINEHAUL filter) plus
// org rate-sheet linehaul LOS from get_lh_los_short_list for the Standard group.
export const getLinehaulLevelOfServices = async (
  accountCode,
  label_code = "",
  orgId,
  accountId,
  loadingFunction = () => {}
) => {
  if (isEmpty(accountCode) || isEmpty(orgId)) {
    return { success: false, data: [], standardLinehaulLos: [] };
  }
  loadingFunction(true);
  try {
    const [accountResult, lhLosResult, rsRes] = await Promise.all([
      fetchAccountShortLos(accountCode, "LH", "account_code", label_code),
      fetchAccountAndRateSheetLos(orgId, accountId),
      fetchRateSheet(orgId),
    ]);
    const standard = (rsRes?.rate_sheets || []).find((rs) => rs.is_default);
    const rateSheetLos = lhLosResult.success ? (lhLosResult.account?.rate_sheet_los || []) : [];
    const standardLinehaulLos = standard
      ? rateSheetLos
        .filter((o) => String(o.rate_sheet_id) === String(standard.id))
        .map((o) => ({
          id: String(o.id),
          code: o.code,
          name: o.name,
          rate_sheet_id: String(standard.id),
        }))
      : [];

    if (accountResult.success) {
      return {
        success: true,
        data: accountResult.account.account_los || [],
        standardLinehaulLos,
      };
    }
    renderAlertMessage(accountResult.errors);
    return { success: false, data: [], standardLinehaulLos, errors: accountResult.errors || [] };
  } catch (error) {
    console.error("Error fetching linehaul level of services:", error);
    throw error;
  } finally {
    loadingFunction(false);
  }
};

export const getAccountConfigs = async (
    account_id,
    organization_id,
    loadingFunction = () => {}
  ) => {
    if (!isEmpty(account_id)) {
      loadingFunction(true);
      try {
        const config = {}
        const response = await fetchAccountConfigs(account_id, organization_id)
        if (response.success) {
            const accountConfigurations = response.account_configurations || [];
            accountConfigurations.forEach((setting) => {
              config[setting.setting_name] = setting.setting_value;
            });
            if (
              !isEmpty(config.pod_pic_settings) &&
              _.isString(config.pod_pic_settings) &&
              config.pod_pic_settings === "[]"
            ) {
              config.pod_pic_settings = [];
            }
            config.pod_pic_settings = updatePicSettingsWithBol(
              config.pod_pic_settings
            );
            if (isEmpty(config.communication_preference)) {
              config.communication_preference = "SMSES";
            }
            return { success: true, data: {...config}, errors: response.errors || [] };
        } else {
          renderAlertMessage(response.errors);
          return { success: true, data: [], errors: response.errors || [] };
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        throw error; // Propagate the error to be handled by the caller
      } finally {
        loadingFunction(false);
      }
    }
  };

  export const getComponentDefs = async (
    loadingFunction = () => {}
  ) => {
      loadingFunction(true);
      try {
        const result = await fetchAccessorialsComponents();
        if (result.success) {
          const orgComponents = result.components || []
        const displayComponents = []
        const noParamComponents = []
        orgComponents.forEach((component) => {
          if (
            component.component_params &&
            component.component_params.length > 0
          ) {
            const componentParams = component.component_params || []
            const visibleParams = componentParams.filter((param) =>
              param.visible_to.includes('DISPATCHER'),
            )
            if (visibleParams.length > 0) {
              displayComponents.push(component.code)
            } else {
              noParamComponents.push(component.code)
            }
          } else {
            noParamComponents.push(component.code)
          }
        })
          return { success: true, data: {
            orgComponents,
            displayComponents,
            noParamComponents,
          }, errors: result.errors || [] };
        } else {
          renderAlertMessage(result.errors);
          return { success: false, data: {
            orgComponents: [],
            displayComponents: [],
            noParamComponents: [],
          }, errors: result.errors || [] };
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        throw error; // Propagate the error to be handled by the caller
      } finally {
        loadingFunction(false);
      }
  };

  const getDeifinitions = ( orgComponents, displayComponents, accessorial) => {
    if (!isEmpty(accessorial)) {
      const components = accessorial.components || []
      const accComponents = components.map(
        (component) => component.component_code,
      )
      const componentDefs = orgComponents.filter(
        (component) =>
          accComponents.includes(component.code) &&
          displayComponents.includes(component.code),
      )
      return componentDefs
    }
    return []
  }

  // const getSlotDetails = (id, reqCode = '') => {
  //   getAccessorialDetails(id, reqCode)
  // }

  export const getAccountComponents = async (
    orgId, 
    accountId,
    orgComponents, 
    displayComponents,
    loadingFunction = () => {}
  ) => {
      loadingFunction(true);
      try {
        const result = await fetchAccountAccessorials(orgId, accountId, 'DISPATCHER');
        if(result.success) {
          const accessorials = result.accessorials || []
          const accountComponentAccessorial = accessorials.map(
            (accessorial) => {
              const componentDefs = getDeifinitions( orgComponents, displayComponents, accessorial)
              return Object.assign({}, accessorial, {
                componentDefs,
              })
            },
          )
          return { success: false, data:  _.sortBy(accountComponentAccessorial, (item) => item.accessorial_name.toLowerCase()), errors: result.errors || [] };
        } else {
          renderAlertMessage(result.errors);
          return { success: false, data: [], errors: result.errors || [] };
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        throw error; // Propagate the error to be handled by the caller
      } finally {
        loadingFunction(false);
      }
  };


  export const getUndeliveredCount = async (
    orgId,
    whIds = "",
    loadingFunction = () => {}
  ) => {
    if (!orgId || !whIds) return "";
    loadingFunction(true);
    try {
      const result = await fetchUndeliveredCount({ orgId, whIds });
      if (result.success) {
        return result.count || "";
      } else {
        renderAlertMessage(result.errors);
        return result.count || "";
      }
    } catch (error) {
      return "";
    } finally {
      loadingFunction(false);
    }
  };
  

// getAccountConfigs = () => {
//     const { config, formData } = this.state;
//     if (formData.account_code) {
//       fetchAccountConfigs(formData.account_id, formData.organization_id).then(
//         (result) => {
//           if (result.success) {
//             const accountConfigurations = result.account_configurations || [];
//             accountConfigurations.forEach((setting) => {
//               config[setting.setting_name] = setting.setting_value;
//             });
//             if (
//               !isEmpty(config.pod_pic_settings) &&
//               _.isString(config.pod_pic_settings) &&
//               config.pod_pic_settings === "[]"
//             ) {
//               config.pod_pic_settings = [];
//             }
//             config.pod_pic_settings = updatePicSettingsWithBol(
//               config.pod_pic_settings
//             );
//             if (isEmpty(config.communication_preference)) {
//               config.communication_preference = "SMSES";
//             }

//             const newState = {
//               config
//             }
//             if (this.state.isNew) {
//               newState.nsr = config['pod_signature'] === 'true' || isEmpty(config['pod_signature']);
//             }
//             this.setState({
//               ...newState
//             });
//           } else {
//             renderAlertMessage(result.errors)
//           }
//         }
//       );
//     } else {
//       this.setState({
//         config: {},
//       });
//     }
//   };
